import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import { Label } from '@/components/shadcn/label'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/shadcn/dialog'
import { WalletTypeSelect } from '@/components/common/WalletTypeSelect'
import { usePutApiCompaniesCurrentShopmodulesShopModuleIdWalletType } from '@/api/generated/company-management-user/company-management-user'
import { ShopModuleTypeIcon, shopModuleLabel } from '@/pages/groups/components/ShopModuleIcon'
import { notify } from '@/lib/notify'
import type { BaseShopModuleModel } from '@/api/model'

interface Props {
  module: BaseShopModuleModel
  onClose: () => void
  /** Called after a successful save so the page can invalidate the module list. */
  onSaved: () => void
}

/**
 * Editor for a single shop module. Shop modules have no name of their own, so the
 * translated type label plus the fixed type icon identify the module in the header —
 * the same pair the card shows.
 */
export function EditShopModuleDialog({ module, onClose, onSaved }: Props) {
  // `groups` is declared because shopModuleLabel() reads its type labels from that NS.
  const { t } = useTranslation(['shop-modules', 'groups', 'common'])
  const [walletTypeId, setWalletTypeId] = useState<string | null>(module.walletTypeId ?? null)

  // Stopgap: the wallet type is the only editable property today, so it is written through
  // the dedicated `PUT /api/companies/current/shopmodules/{id}/wallet-type` endpoint.
  // TODO: as soon as editing of the shop module is extended, drop this per-property call and
  // patch the whole shop module entity instead — that endpoint does not exist in the backend
  // yet and has to be added with the first additional field.
  const save = usePutApiCompaniesCurrentShopmodulesShopModuleIdWalletType()

  async function handleSave() {
    try {
      await save.mutateAsync({ shopModuleId: module.id, data: { walletTypeId } })
      notify.success(t('toasts.saved'))
      onSaved()
      onClose()
    } catch (error) {
      notify.apiError(error, t('toasts.saveError'))
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShopModuleTypeIcon type={module.type} className="size-5" />
            {t('editDialog.title', { type: shopModuleLabel(module.type) })}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-1.5 py-2">
          <Label>{t('editDialog.walletType')}</Label>
          <WalletTypeSelect value={walletTypeId} onChange={setWalletTypeId} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('buttons.cancel', { ns: 'common' })}</Button>
          <Button onClick={handleSave} disabled={save.isPending}>{t('buttons.save', { ns: 'common' })}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
